import { observer } from "mobx-react-lite";
import {
  type InputDescription,
  type IntegrationDescription,
  unwrapValueConfig,
  type ValueConfig,
  type ValueDescription,
  wrapValueConfig,
} from "@smartsuite/automation-engine-api";
import type {
  ActionCandidate,
  InputCandidate,
  TriggerCandidate,
} from "../../../../../../../../types/candidate";
import {
  Icon,
  KeyValueCustomSelect,
  Paragraph,
  Tooltip,
} from "@smartsuite/react-ui/lib";
import { useEffect, useState } from "react";
import { JsonEditor } from "./JsonEditor";
import { toJS } from "mobx";
import { StyledContainerWrapper } from "../../../style";
import { CaptionBlock } from "../../../../../../../../components/CaptionBlock";
import { ValueDescription_T } from "@smartsuite/automation-engine-api";
import { StringValue } from "./StringValue/StringValue";
import { NumberValue } from "./NumberValue/NumberValue";
import { BooleanValue } from "./BooleanValue/BooleanValue";
import { StringEditor } from "../StringEditor";
import { NumberEditor } from "../NumberEditor";
import { useTranslation } from "react-i18next";
import { useRootStore } from "../../../../../../../../stores/hooks";
import { renderOption } from "./utils/render-options";
import { options } from "./utils/options";
import { emptyValueByType, getInputValueType } from "./utils/inputs";
import * as R from "remeda";
import { checkInputValidation } from "../../../../../../../../stores/automation/utils/inputValidation";
import { getDescriptionTypeByValue } from "../../../../utils";
import { updateTriggerDescription } from "../../../../../../../../services/updateDescription";
import { useEngineClient } from "../../../../../../../../services/engineClient";
import * as S from "./style";
import { IconColorNames } from "@smartsuite/types";
import { CircleLoaderIcon } from "@smartsuite/react-ui/lib/components/loaders/CircleLoaderIcon/CircleLoaderIcon";
import { translateIfPresent } from "../../../../../../../../locales/i18n";
import { useFieldMissingValidation } from "../../../../../../../../stores/automation/hooks/field-missing-validation";
import { FieldMissingError } from "../../../../../../Sections/ErrorSection";
import { ListReferenceEditor } from "../ListReferenceEditor";
import { withInstruction } from "./with-instructions";
import { requiresObject } from "./requires-object";

export const JsonEditorWrapper = observer<{
  description: InputDescription;
  input: InputCandidate;
  onChange: (input: InputCandidate) => void;
  showValidationError: boolean;
  action?: ActionCandidate;
  actions: ActionCandidate[];
  trigger: TriggerCandidate;
  integrations: IntegrationDescription[];
  isError?: boolean;
  onClickTrash?: () => void;
}>(
  ({
    description,
    input,
    onChange,
    showValidationError,
    isError,
    onClickTrash,
    action,
    actions,
    trigger,
    integrations,
  }) => {
    const store = useRootStore();
    const { t } = useTranslation();
    const engineClient = useEngineClient();
    const [open, setOpen] = useState<boolean>(false);
    const [inputValueType, setInputValueType] = useState<string>("");
    const [isInputValueTypeChanged, setIsInputValueTypeChanged] =
      useState(false);
    const [onlyStructure, setOnlyStructure] = useState<boolean>(false);
    const [validationError, setValidationError] = useState<string>("");
    const [defaultValue, setDefaultValue] = useState<ValueConfig | undefined>();
    const [reconfigurable, setReconfigurable] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);
    const { getFieldMissingErrorMessage } = useFieldMissingValidation();
    const fieldMissingMessage = getFieldMissingErrorMessage(input, action);
    const {
      automations: { candidate },
    } = store;

    useEffect(() => {
      setInputValueType(
        getInputValueType(
          input,
          isInputValueTypeChanged ? undefined : description,
        ),
      );
      setOnlyStructure(
        description.editor?.editor?.oneofKind === "jsonStructure",
      );

      if (description.editor?.editor?.oneofKind === "jsonStructure") {
        setDefaultValue(description.editor.editor.jsonStructure.defaultValue);
        setReconfigurable(
          !!description.editor.editor.jsonStructure.reconfigurable,
        );
      }

      if (description.editor?.editor?.oneofKind === "json") {
        setDefaultValue(description.editor.editor.json.defaultValue);
        setReconfigurable(!!description.editor.editor.json.reconfigurable);
      }
    }, [input.valueDescription?.type]);

    useEffect(() => {
      if (input.value === undefined && !reconfigurable) {
        onChange({
          ...input,
          value: {
            values: [],
          },
        });
      }
    }, [input]);

    useEffect(() => {
      if (defaultValue !== undefined) {
        onChange({
          ...input,
          value: defaultValue,
          valueDescription: {
            type: getDescriptionTypeByValue(defaultValue),
            multiple: false,
          },
        });
        setInputValueType(
          getInputValueType(
            undefined,
            undefined,
            getDescriptionTypeByValue(defaultValue),
          ),
        );
      }
    }, [defaultValue]);

    useEffect(() => {
      if (showValidationError) {
        if (R.isTruthy(description)) {
          setValidationError(checkInputValidation(description, input));
        }
      } else {
        setValidationError("");
      }
    }, [input, description, showValidationError]);

    const onOpendChange = (e: boolean): void => {
      setOpen(e);
    };

    useEffect(() => {
      if (withInstruction(description) || requiresObject(description)) {
        setInputValueType("json_object");
      }
    }, [description]);

    const onSelectedOptionChange = (value: string, accept = false): void => {
      if (!accept && inputValueType !== "") {
        store.modal.openConfirmModal({
          type: "warning",
          title: t("confirm-modal.are-you-sure"),
          description: t("confirm-modal.want-to-change-response-type"),
          positiveButtonText: t("confirm-modal.yes-change-response-type"),
          negativeButtonText: t("confirm-modal.no-cancel"),
          onClose: () => {
            store.modal.closeConfirmModal();
          },
          onConfirm: () => {
            onSelectedOptionChange(value, true);
            store.modal.closeConfirmModal();
          },
        });
        return;
      }
      let configByValue: ValueConfig = {
        values: [emptyValueByType(value)],
      };
      let valueDescription: ValueDescription = {
        type: ValueDescription_T.KEY_VALUE,
        multiple: false,
      };
      switch (value) {
        case "text":
          configByValue = onlyStructure ? configByValue : { values: [] };
          valueDescription = {
            type: ValueDescription_T.STRING,
            multiple: false,
          };
          break;
        case "number":
          configByValue = onlyStructure ? configByValue : { values: [] };
          valueDescription = {
            type: ValueDescription_T.NUMBER,
            multiple: false,
          };
          break;
        case "boolean":
          configByValue = onlyStructure ? configByValue : { values: [] };
          valueDescription = {
            type: ValueDescription_T.BOOL,
            multiple: false,
          };
          break;
        case "json_object":
          configByValue = { values: [] };
          valueDescription = {
            type: ValueDescription_T.KEY_VALUE,
            multiple: false,
          };
          break;
        case "json_array":
          valueDescription = {
            type: ValueDescription_T.LIST,
            multiple: false,
          };
          break;
        case "list":
          valueDescription = {
            type: ValueDescription_T.LIST,
            multiple: false,
          };
          break;
      }
      setInputValueType(value);
      setIsInputValueTypeChanged(true);
      onChange({
        ...input,
        value: configByValue,
        valueDescription,
      });
    };

    const onChangeField = (input: InputCandidate): void => {
      if (
        input.value?.values.some(
          (v) =>
            v.value.oneofKind === "keyValue" || v.value.oneofKind === "list",
        )
      ) {
        onChange({
          ...input,
          value: wrapValueConfig(input.value),
        });
        return;
      }
      onChange({ ...input });
    };

    const onClickReconfigure = (): void => {
      setLoading(true);
      setInputValueType("");
      onChange({
        ...input,
        value: undefined,
      });

      void updateTriggerDescription(
        candidate,
        false,
        engineClient,
        store.automations,
        store.integrations,
      ).finally(() => {
        setLoading(false);
      });
    };

    return (
      <StyledContainerWrapper>
        {description && (
          <CaptionBlock description={description} onClickTrash={onClickTrash} />
        )}
        <S.SelectorWrapper>
          {!withInstruction(description) && !requiresObject(description) && (
            <KeyValueCustomSelect
              my="left top+2"
              state={isError ? "error" : open ? "active" : "default"}
              value={inputValueType}
              disabled={false}
              placeholder={t("automation-form.select-body-type")}
              showClear={false}
              renderOption={(option) =>
                renderOption(option, inputValueType, onSelectedOptionChange)
              }
              renderSelectedOption={(option) =>
                renderOption(
                  option,
                  inputValueType,
                  onSelectedOptionChange,
                  true,
                )
              }
              placeholderArrowStyle="filled-dark"
              options={options(false, false, !onlyStructure)}
              className={open ? "container-is-open" : ""}
              onOpenedChange={onOpendChange}
              onChange={(value) => onSelectedOptionChange(String(value))}
              styles={{ marginBottom: "10px" }}
            />
          )}

          {reconfigurable && (
            <Tooltip
              text={
                candidate.isActiveStatus()
                  ? translateIfPresent(
                      "caption.Reconfiguration is disabled while automation is active",
                      "Reconfiguration is disabled while automation is active",
                    )
                  : translateIfPresent(
                      "caption.Redetermine data structure",
                      "Redetermine data structure",
                    )
              }
              placements={["top", "bottom"]}
              padding={0}
              target={
                <S.ReconfigureSection>
                  {loading ? (
                    <CircleLoaderIcon />
                  ) : (
                    <Icon
                      name={"refresh"}
                      size={"M"}
                      color={
                        candidate.isActiveStatus()
                          ? IconColorNames.light
                          : IconColorNames.dark
                      }
                      onClick={() => {
                        // if it is active, we should not reconfigure
                        if (candidate.isActiveStatus()) {
                          return;
                        }
                        onClickReconfigure();
                      }}
                      transition={true}
                    />
                  )}
                </S.ReconfigureSection>
              }
            />
          )}
        </S.SelectorWrapper>
        {["json_object", "json_array"].includes(inputValueType) && (
          <JsonEditor
            description={description}
            input={toJS({
              ...input,
              label: description.label,
              value: input.value ? unwrapValueConfig(input.value) : undefined,
            })}
            config={input.value ? unwrapValueConfig(input.value) : undefined}
            onlyStructure={onlyStructure}
            onChange={onChangeField}
            action={action}
            isError={fieldMissingMessage !== ""}
          />
        )}
        {inputValueType === "text" && onlyStructure && (
          <StringValue
            value={input.value}
            label={"Text"}
            onlyStructure={onlyStructure}
            action={action}
          />
        )}
        {inputValueType === "text" && !onlyStructure && (
          <StringEditor
            onChange={onChangeField}
            input={toJS({
              ...input,
              label: description.label,
            })}
            trigger={trigger}
            isMultiple={false}
            actions={actions}
            action={action}
            integrations={integrations}
            showValidationError={showValidationError}
            isError={false}
          />
        )}
        {inputValueType === "number" && onlyStructure && (
          <NumberValue
            value={input.value?.values[0]}
            label={"Number"}
            onlyStructure={onlyStructure}
          />
        )}
        {inputValueType === "number" && !onlyStructure && (
          <NumberEditor
            allowNegative={true}
            onChange={onChangeField}
            config={toJS({
              ...input,
              label: description.label,
            })}
            trigger={trigger}
            actions={actions}
            action={action}
            integrations={integrations}
            isError={showValidationError}
          />
        )}
        {inputValueType === "boolean" && onlyStructure && (
          <BooleanValue
            value={input.value?.values[0]}
            label={"Boolean"}
            onlyStructure={onlyStructure}
          />
        )}
        {inputValueType === "boolean" && !onlyStructure && (
          <KeyValueCustomSelect
            my="left top+2"
            value={
              input.value?.values[0]?.value?.oneofKind === "bool"
                ? input.value?.values[0].value.bool
                : undefined
            }
            disabled={false}
            placeholder={t("automation-form.select-yes-or-no")}
            showClear={false}
            placeholderArrowStyle="filled-dark"
            options={[
              { value: true, label: t("caption.Yes"), iconName: "yes" },
              { value: false, label: t("caption.No"), iconName: "no" },
            ]}
            onChange={(value) =>
              onChangeField({
                ...input,
                value: {
                  values: [
                    {
                      value: {
                        oneofKind: "bool",
                        bool: Boolean(value),
                      },
                    },
                  ],
                },
              })
            }
          />
        )}
        {inputValueType === "list" && !onlyStructure && (
          <ListReferenceEditor
            onChange={onChangeField}
            input={toJS({
              ...input,
              label: description.label,
            })}
            trigger={trigger}
            actions={actions}
            action={action}
            integrations={integrations}
            showValidationError={showValidationError}
            isError={false}
          />
        )}
        {validationError !== "" && (
          <Paragraph color={"error"} size={"xs"}>
            {validationError}
          </Paragraph>
        )}
        <FieldMissingError message={fieldMissingMessage} />
      </StyledContainerWrapper>
    );
  },
);
JsonEditorWrapper.displayName = "JsonFieldEditor";
